import type { AxiosRequestConfig } from 'axios';

import type { PaginationResponse } from '~/api/Catalog';
import type { KYCStatus } from '~/api/KYCStatus';
import { mainStore } from '~/stores';
import type { PaginationRequest } from '~/stores/CatalogStore';
import type { ProductReview } from '~/types/Product/interface';

import { ENDPOINT } from './constants';
import type { Address } from './DeliveryAddress';
import type { OrderShort } from './Order';
import { RequestAPI } from './Requests';

export enum SubscriptionFlagsNames {
  NEWS = 'news',
  REMINDERS = 'reminders',
  RECOMMENDATIONS = 'recommendations',
  SPECIAL_DEALS = 'special_deals',
  NEWS_EMAIL = 'news_email',
  REMINDERS_EMAIL = 'reminders_email',
  RECOMMENDATIONS_EMAIL = 'recommendations_email',
  SPECIAL_DEALS_EMAIL = 'special_deals_email',
}

export enum VerificationType {
  AUDKENNI = 'audkenni',
  MOBILE = 'mobile',
}

export interface SubscriptionFlag {
  name: SubscriptionFlagsNames;
  enabled?: boolean;
  push?: boolean;
  email?: string;
}

export interface UpdatePersonalFields {
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  phone?: string;
  date_of_birth?: string | null;
  email?: string | null;
  age_restriction?: boolean;
  app_rating?: boolean;
}

interface PersonalAddress {
  street_address_1: string;
  street_address_2: string;
  city: string;
  postcode: string;
  customer_id: string;
  country: string;
  place_id: string;
  latitude: number;
  longitude: number;
  id: string;
}

export interface RequestCodeResponse {
  body: string;
  isDeleted?: boolean;
  to: string;
  err: null;
}

export interface DokobitRequestCodeResponse {
  method: 'dokobit';
  data: Nullable<{
    control_code: string;
    status: string;
    token: string;
  }>;
}

export interface VerifyCodeResponse {
  customer: Omit<Customer, 'address' | 'subscription' | 'customer_type'>;
  addresses: Address[];
  verified: boolean;
  access_token: string;
  refresh_token: string;
}

interface UpdatePersonalDataResponse {
  customer?: Omit<Customer, 'app_rating' | 'type'>[];
}

interface CustomerType {
  code: 'User' | 'Staff';
  created_at: string;
  description: string;
  id: number;
  name: 'User' | 'Staff';
  privileges: Record<string, unknown>;
  updated_at: string;
}

export interface Customer {
  address: PersonalAddress[];
  date_of_birth: string | null;
  default_address_id: string | null;
  email: string | null;
  first_name: string | null;
  id: string;
  last_login: string | null;
  last_name: string | null;
  middle_name: string | null;
  phone: string | null;
  subscription: Subscription[];
  age_restriction: boolean | null;
  app_rating: boolean;
  freeDeliveryDaysLeft: number;
  type: CustomerType | null;
  customer_type: CustomerType | null;
  kyc_verification_status: KYCStatus;
  verification_status: string | null;
  identification_number: string | null;
}

interface Subscription {
  channel_data: null;
  created_at: string;
  customer_id: string;
  deleted_at: string | null;
  device_id: string;
  id: string;
  updated_at: string;
  subscription_flags: SubscriptionFlag[];
}

interface Device {
  created_at: string;
  customer_id: string;
  deleted_at: string | null;
  email: string | null;
  email_player_id: string | null;
  id: string;
  last_session: string;
  name: string | null;
  player_id: string | null;
  push_token: string | null;
  updated_at: string;
  subscriptions: Subscription | null;
}

export interface BonusesTransaction {
  id: number;
  customer_id: string;
  order_id: string;
  reason: 'order_delivered' | 'applied_to_basket' | 'refund' | 'promo';
  amount: number;
  created_at: string;
  updated_at: string;
  type: 'charge' | 'add';
}

export interface LoyaltyPoints {
  id: string;
  customerId: string;
  balance: number;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
  companyId: string;
}

export type LoyaltyBalance = LoyaltyPoints['balance'];

export interface LoyaltyPointsTransaction {
  id: string;
  walletId: string;
  customerId: string;
  transactionType: 'payment' | 'refund' | 'reward' | 'manual_adjustment';
  transactionStatus: 'pending' | 'completed' | 'failed' | 'reversed';
  amount: number;
  referenceId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  companyId: string;
  order: Pick<OrderShort, 'order_id'>;
}

export interface LoyaltyPointsTerms {
  companyId: string;
  configKey: string;
  configValue: {
    type: 'Boolean' | 'Number' | 'String';
    value: boolean | number | string;
  };
  description: string;
  id: string;
  isEditable: boolean;
}

export type RatingDetails = Record<'1' | '2' | '3' | '4' | '5', number>;

export interface ReviewsResponse {
  data: ProductReview[];
  canCreateReview: boolean;
  details: RatingDetails;
  pagination: PaginationResponse;
}

export type VerificationCodeRequestParams =
  | {
      type: VerificationType.MOBILE;
      personal_code?: never;
    }
  | {
      type: VerificationType.AUDKENNI;
      personal_code: string;
    };

export type CustomerRequestCodeParams = {
  phone_number: string;
  device_id: string;
  recaptcha_token: string;
  useDokobit?: boolean;
} & VerificationCodeRequestParams;

export type CheckDokobitStatusParams = {
  token: string;
  phone_number: string;
  recaptcha_token?: string;
  device_id?: string;
  signal?: AbortController['signal'];
} & VerificationCodeRequestParams;

interface IntercomIdentityVerificationResponse {
  data: {
    intercomToken: string;
  };
}

interface CustomerRequestsInterface {
  requestCode(data: CustomerRequestCodeParams): Promise<RequestCodeResponse>;

  verifyCode(data: {
    phone_number: string;
    otp: string;
    device_id: string;
    restore?: boolean;
  }): Promise<VerifyCodeResponse>;

  logout(data: { refresh_token: string; customer_id: string }): Promise<void>;

  deleteProfile(): Promise<void>;

  getPersonalData(
    customerID: string,
  ): Promise<Omit<Customer, 'app_rating' | 'type'>[]>;

  updatePersonalData(
    customerID: string,
    data: UpdatePersonalFields,
  ): Promise<UpdatePersonalDataResponse>;

  updateDeviceData(
    deviceID: string,
    data: {
      name: string;
      email: string;
      subscription_flags: SubscriptionFlag[];
    },
  ): Promise<{ device: Device }>;

  updateSubscription(
    deviceID: string,
    data: {
      subscription_flags: SubscriptionFlag[];
    },
  ): Promise<{ subscription: Subscription }>;

  refreshToken(data: {
    refresh_token: string;
    customer_id: string;
  }): Promise<{ access_token: string; refresh_token: string }>;

  createDevice(): Promise<{ device: Device }>;

  requestBonuses(): Promise<number>;

  requestBonusesTransactions(data: {
    'pagination[page]': number;
    'pagination[size]': number;
  }): Promise<{ items: BonusesTransaction[]; pagination: { total: number } }>;

  requestLoyaltyPoints(
    options?: AxiosRequestConfig,
  ): Promise<{ data: LoyaltyBalance; status: string }>;

  requestLoyaltyPointsTransactions(
    data: {
      'pagination[page]': number;
      'pagination[size]': number;
    },
    options?: AxiosRequestConfig,
  ): Promise<{ data: LoyaltyPointsTransaction[]; status: string }>;

  requestLoyaltyPointsTerms(
    conpanyId: string,
  ): Promise<{ data: LoyaltyPointsTerms[]; status: string }>;

  requestKYCSession(data: {
    redirectUrl?: string;
  }): Promise<{ data: { url: string } }>;

  // TODO: rewrite it to use correct slugs, not id slugs
  requestReviewsByProductId(data: {
    productId: number | string;
    page: PaginationRequest['page'];
  }): Promise<ReviewsResponse>;

  addNewReview(data: {
    productId: number;
    mark: 0 | 1 | 2 | 3 | 4 | 5;
    content: string | null;
  }): Promise<{ data: any }>;

  checkDokobitStatus(data: CheckDokobitStatusParams): Promise<{
    data: Nullable<{
      access_token: string;
      refresh_token: string;
      customer: Customer;
      addresses: Address[];
      verified: boolean;
      method: 'sms' | undefined;
    }>;
  }>;

  sendDokobitCode(
    data: CustomerRequestCodeParams,
  ): Promise<DokobitRequestCodeResponse>;

  requestIntercomIdentityVerification(): Promise<IntercomIdentityVerificationResponse>;
}

export const CustomerRequests: CustomerRequestsInterface = {
  requestCode: (data) => RequestAPI.get(ENDPOINT.customer.code.request, data),

  verifyCode: (data) =>
    RequestAPI.post(
      ENDPOINT.customer.code.verify + mainStore.convertObjToGet(data),
    ),

  logout: (data) => RequestAPI.post(ENDPOINT.customer.logout, data),

  deleteProfile: () => RequestAPI.delete(ENDPOINT.customer.personalData.delete),

  getPersonalData: (customerID) =>
    RequestAPI.get(ENDPOINT.customer.personalData.request + customerID),

  updatePersonalData: (customerID, data) =>
    RequestAPI.post(ENDPOINT.customer.personalData.update + customerID, data),

  updateDeviceData: (deviceID, data) =>
    RequestAPI.post(ENDPOINT.customer.deviceData.update + deviceID, data),

  updateSubscription: (deviceID, data) =>
    RequestAPI.post(ENDPOINT.customer.subscription.update + deviceID, data),

  refreshToken: (data) =>
    RequestAPI.refresh(ENDPOINT.customer.token.refresh, data),

  createDevice: () => RequestAPI.post(ENDPOINT.customer.deviceData.create),

  requestBonuses: () => RequestAPI.get(ENDPOINT.bonuses.balance),

  requestBonusesTransactions: (data) =>
    RequestAPI.get(ENDPOINT.bonuses.history, data),

  requestLoyaltyPoints: () => RequestAPI.get(ENDPOINT.loyaltyPoints.balance),

  requestLoyaltyPointsTransactions: (data) =>
    RequestAPI.get(ENDPOINT.loyaltyPoints.history, data),

  requestLoyaltyPointsTerms: (companyId) =>
    RequestAPI.get(ENDPOINT.loyaltyPoints.terms + companyId),

  requestKYCSession: (data) =>
    RequestAPI.get(ENDPOINT.customer.kyc.status, data),

  requestReviewsByProductId: (data) =>
    RequestAPI.get(ENDPOINT.customer.reviews.requestAll, data),

  addNewReview: (data) =>
    RequestAPI.post(ENDPOINT.customer.reviews.newReview, data),

  checkDokobitStatus: async (data) => {
    const { signal, ...payload } = data;
    const response = await RequestAPI.get(
      ENDPOINT.customer.dokobit.check,
      payload,
      {
        signal,
      },
    );

    return { data: response ?? null };
  },

  sendDokobitCode: (data) =>
    RequestAPI.get(ENDPOINT.customer.dokobit.send, data),

  requestIntercomIdentityVerification: () =>
    RequestAPI.get(ENDPOINT.customer.intercom.identityVerification),
};
